import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { CheckCircle, XCircle, CircleNotch, ArrowRight } from "@phosphor-icons/react";

export default function PaymentReturn() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ref = params.get("ref");
  const provider = params.get("provider");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const isSumup = provider === "sumup" || (!sessionId && ref);
    if (!sessionId && !ref) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 6) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = isSumup
          ? await api.get(`/payments/sumup/status?ref=${encodeURIComponent(ref)}`)
          : await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch { if (active) setTimeout(poll, 2000); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, ref, provider]);

  const btn = "inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-medium text-sm uppercase tracking-[0.14em] px-8 py-4 hover:bg-[#00227A] transition-all duration-300";
  const outline = "inline-flex items-center gap-2 border border-[#0B1533] text-[#0B1533] font-body font-medium text-sm uppercase tracking-[0.14em] px-8 py-4 hover:bg-[#0B1533] hover:text-white transition-colors duration-300";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="payment-return">
      {state === "checking" && (
        <div data-testid="pay-checking">
          <CircleNotch size={48} className="animate-spin text-[#EF5A1E] mx-auto" />
          <h1 className="font-heading font-light tracking-tighter text-3xl mt-6">{t("pay_checking")}</h1>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="pay-success">
          <div className="w-16 h-16 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto"><CheckCircle size={36} weight="fill" color="#fff" /></div>
          <h1 className="font-heading font-light tracking-tighter text-4xl mt-6">{t("pay_success_title")}</h1>
          <p className="font-body text-[#5A607A] mt-3 text-lg">{t("pay_success_sub")}</p>
          {ref && <p className="font-body text-sm mt-4">{t("ref")}: <span className="text-[#EF5A1E] font-semibold">{ref}</span></p>}
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            {ref && <Link to={`/track?ref=${ref}`} className={btn} data-testid="pay-track-link">{t("track_your_ride")} <ArrowRight size={16} /></Link>}
            <Link to="/" className={outline}>{t("nav_book")}</Link>
          </div>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div data-testid="pay-failed">
          <div className="w-16 h-16 bg-[#EF4444] rounded-full flex items-center justify-center mx-auto"><XCircle size={36} weight="fill" color="#fff" /></div>
          <h1 className="font-heading font-light tracking-tighter text-4xl mt-6">{state === "timeout" ? t("pay_pending_title") : t("pay_failed_title")}</h1>
          <p className="font-body text-[#5A607A] mt-3 text-lg">{state === "timeout" ? t("pay_pending_sub") : t("pay_failed_sub")}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            {ref && <Link to={`/manage?ref=${ref}`} className={btn} data-testid="pay-manage-link">{t("view_details")} <ArrowRight size={16} /></Link>}
            <Link to="/" className={outline}>{t("nav_book")}</Link>
          </div>
        </div>
      )}
    </div>
  );
}
